import type { LayerSpec } from '@/core/pipeline';
import { lerpRGB, parseHex, type RGB } from '@/lib/color';

type GradientParams = {
  type: 'linear' | 'radial' | 'conic';
  angle: number;     // degrees, linear only
  a: string;         // hex color (start)
  b: string;         // hex color (end)
  power: number;     // easing exponent
};

export const gradientSpec: LayerSpec = {
  kind: 'gradient',
  category: 'generator',
  animated: false,
  label: 'Gradient',
  defaults: {
    type: 'linear',
    angle: 0,
    a: '#1d2b53',
    b: '#ff004d',
    power: 1,
  } satisfies GradientParams,
  schema: {
    type: {
      type: 'select',
      label: 'Type',
      options: ['linear', 'radial', 'conic'],
      default: 'linear',
    },
    angle: { type: 'number', label: 'Angle', min: 0, max: 360, step: 1, default: 0 },
    a:     { type: 'color',  label: 'Start', default: '#1d2b53' },
    b:     { type: 'color',  label: 'End',   default: '#ff004d' },
    power: { type: 'number', label: 'Curve', min: 0.1, max: 4, step: 0.05, default: 1 },
  },
  run: (_input, params, ctx) => {
    const p = params as GradientParams;
    const colA = parseHex(p.a) ?? ([0, 0, 0] as RGB);
    const colB = parseHex(p.b) ?? ([255, 255, 255] as RGB);
    const out = new ImageData(ctx.width, ctx.height);
    const data = out.data;
    const w = ctx.width, h = ctx.height;
    const cx = (w - 1) / 2, cy = (h - 1) / 2;
    const power = p.power || 1;

    if (p.type === 'linear') {
      const rad = (p.angle * Math.PI) / 180;
      const dx = Math.cos(rad), dy = Math.sin(rad);
      // Project pixel onto direction; normalize to [0,1] across the bounding box.
      const range = Math.abs(dx) * w + Math.abs(dy) * h;
      const off = (Math.min(0, dx) * (w - 1) + Math.min(0, dy) * (h - 1));
      let i = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++, i += 4) {
          const t = clamp01((x * dx + y * dy - off) / range);
          const u = Math.pow(t, power);
          const c = lerpRGB(colA, colB, u);
          data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
        }
      }
    } else if (p.type === 'radial') {
      const maxR = Math.hypot(cx, cy);
      let i = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++, i += 4) {
          const t = clamp01(Math.hypot(x - cx, y - cy) / maxR);
          const u = Math.pow(t, power);
          const c = lerpRGB(colA, colB, u);
          data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
        }
      }
    } else { // conic
      const rad0 = (p.angle * Math.PI) / 180;
      let i = 0;
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++, i += 4) {
          let a = Math.atan2(y - cy, x - cx) - rad0;
          a = ((a % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
          const t = a / (2 * Math.PI);
          const u = Math.pow(t, power);
          const c = lerpRGB(colA, colB, u);
          data[i] = c[0]; data[i+1] = c[1]; data[i+2] = c[2]; data[i+3] = 255;
        }
      }
    }
    return out;
  },
};

function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}
